import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { FormInput } from './FormInput';

interface CreateBudgetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

export const CreateBudgetDialog: React.FC<CreateBudgetDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [newBudgetName, setNewBudgetName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Petit délai pour s'assurer que le dialogue est complètement ouvert
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setNewBudgetName('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!newBudgetName.trim()) return;
    onConfirm(newBudgetName.trim());
    setNewBudgetName('');
    onClose();
  };

  const handleClose = () => {
    setNewBudgetName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50" />

        <div className="relative mx-4 w-full max-w-md rounded-lg bg-white p-8 dark:bg-surface-800">
          <Dialog.Title className="mb-4 text-lg font-medium text-slate-900 dark:text-white">
            {t('budget.new')}
          </Dialog.Title>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 gap-6">
              <FormInput
                label={t('budget.name')}
                value={newBudgetName}
                onChange={(value) => setNewBudgetName(value as string)}
                required
                placeholder={t('budget.name')}
                ref={inputRef}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md border border-slate-500 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-700 dark:text-slate-300 dark:hover:bg-surface-600"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                data-attr="create-budget-button"
                disabled={!newBudgetName.trim()}
                className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
              >
                {t('common.add')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
};
